"use client";

import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { motion } from "framer-motion";
import { useState, useCallback, useEffect, useMemo } from "react";
import BookingIcon from "@/src/shared/components/icons/BookingIcon";
import ExploreIcon from "@/src/shared/components/icons/ExploreIcon";
import InboxIcon from "@/src/shared/components/icons/InboxIcon";
import ProfileIcon from "@/src/shared/components/icons/ProfileIcon";
import WishlistIcon from "@/src/shared/components/icons/WishlistIcon";

interface IBottomNavigationProps {
  isVisible: boolean;
}

interface INavIconProps {
  size?: string | number;
  className?: string;
}

interface INavItem {
  id: string;
  icon: React.ComponentType<INavIconProps>;
  label: string;
  href: string;
  isProtected: boolean;
}

const BottomNavigation: React.FC<IBottomNavigationProps> = ({ isVisible }) => {
  const router = useAppRouter();

  // Helper to normalize route path (strip query/hash)
  const normalizePath = (p: string) => p?.split("#")[0]?.split("?")[0] || "/";

  // Map a pathname/asPath to a nav id
  const resolveActiveTab = useCallback((path: string): INavItem["id"] => {
    const p = normalizePath(path);
    if (p === "/") return "explore";
    if (p.startsWith("/wishlists")) return "wishlist";
    if (p.startsWith("/booking")) return "booking";
    if (p.startsWith("/chat")) return "inbox";
    // Notifications open from the profile page's bell, as in the app.
    if (
      p.startsWith("/profile") ||
      p.startsWith("/notifications") ||
      p.startsWith("/inbox") ||
      p === "/earn-by-hosting" ||
      p === "/terms-conditions" ||
      p === "/refund-policy" ||
      p === "/privacy-policy" ||
      p === "/contact-us" ||
      p === "/about"
    )
      return "profile";
    return "explore";
  }, []);

  // Initialize from current route to avoid flash
  const initialActive = useMemo(
    () => resolveActiveTab(router.asPath || router.pathname),
    [resolveActiveTab, router.asPath, router.pathname],
  );
  const [activeTab, setActiveTab] = useState<INavItem["id"]>(initialActive);

  // Keep in sync on route changes and reloads
  useEffect(() => {
    const nextId = resolveActiveTab(router.asPath || router.pathname);
    setActiveTab(nextId);
  }, [router.asPath, router.pathname, resolveActiveTab]);

  const navItems: INavItem[] = [
    {
      id: "explore",
      icon: ExploreIcon,
      label: "Explore",
      href: "/",
      isProtected: false,
    },
    // The app's tab order: Explore, Wishlist, Booking, Inbox, Profile.
    {
      id: "wishlist",
      icon: WishlistIcon,
      label: "Wishlist",
      href: "/wishlists",
      isProtected: true,
    },
    {
      id: "booking",
      icon: BookingIcon,
      label: "Booking",
      href: "/booking",
      isProtected: true,
    },
    {
      id: "inbox",
      icon: InboxIcon,
      label: "Inbox",
      href: "/chat",
      isProtected: true,
    },
    {
      id: "profile",
      icon: ProfileIcon,
      label: "Profile",
      href: "/profile",
      isProtected: true,
    },
  ];

  const handleNavClick = useCallback(
    (item: INavItem) => {
      if (typeof window !== "undefined") {
        const token = window.localStorage.getItem("travela_auth_token");
        if (item.isProtected && !token) {
          void router.push("/signin");
        } else {
          setActiveTab(item.id);
          void router.push(item.href);
        }
      }
    },
    [router],
  );

  return (
    <motion.div
      initial={{ y: 0 }}
      animate={{ y: isVisible ? 0 : 100 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      // The app's tab bar: 24px icons, black when selected and #666870 otherwise, 12px
      // medium labels, 50px rows inside a 5px pad, over the safe area.
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#C3C6D3] bg-white pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <div className="flex p-[5px]">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item)}
              aria-current={isActive ? "page" : undefined}
              className={`flex h-[50px] flex-1 flex-col items-center justify-center ${
                isActive ? "text-black" : "text-ink-gray"
              }`}
            >
              <Icon size={24} />
              <span className="mb-1.5 mt-auto text-xs font-medium leading-4">
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default BottomNavigation;
